'use client'

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react'
import type { CenteringSide, GradeSideAnalysis, GradingProvider } from '../../lib/types'
import { FlipIcon } from '../icons'
import { slabPalette, deriveSlabPaletteFromImage, type SlabPalette } from './palette'
import { SlabHeader } from './SlabHeader'
import { useSlabFlip } from './useSlabFlip'
import { AnalysisOverlayCanvas } from './AnalysisOverlayCanvas'
import styles from './slab.module.css'

interface SlabProps {
  className?: string
  showActions?: boolean
  frontImageUrl: string
  backImageUrl: string
  frontImageRectificationApplied?: boolean
  backImageRectificationApplied?: boolean
  embossFrontImageUrl?: string
  embossBackImageUrl?: string
  cardName: string
  cardNumber: string
  officialCount: string
  setName: string
  language: string
  grade: string
  provider: GradingProvider
  confidencePercent: number | null
  paletteColor?: string
  analysis?: { front?: GradeSideAnalysis; back?: GradeSideAnalysis }
  centering?: { front?: CenteringSide; back?: CenteringSide }
  useInlineLogos?: boolean
  flipEnabled?: boolean
  samplePalette?: boolean
  palette?: SlabPalette
}

/**
 * 2D slab — a flat card that flips on its Y axis (perspective 1200, scaleX -1
 * on the back), ported from the app's owen/grade-page slab. No tilt, no 3D
 * edge. The whole slab rotates as one plane; the card photo cross-fades
 * between front and back at the edge-on point, and the header reads correctly
 * on both faces via the scaleX un-mirror.
 */
export function Slab(props: SlabProps) {
  const stageRef = useRef<HTMLDivElement>(null)
  const flipEnabled = props.flipEnabled ?? true
  const flipRef = useSlabFlip(stageRef, flipEnabled)
  const [surfaceView, setSurfaceView] = useState(false)

  // Seed palette renders immediately (SSR-safe); the sampled palette — the
  // app's deriveHeroPaletteFromPixels — replaces it once the photo is readable.
  const [palette, setPalette] = useState<SlabPalette>(() =>
    slabPalette(props.paletteColor),
  )
  useEffect(() => {
    if (props.palette) return
    if (props.samplePalette === false) {
      setPalette(slabPalette(props.paletteColor))
      return
    }
    let cancelled = false
    deriveSlabPaletteFromImage(props.frontImageUrl).then((sampled) => {
      if (sampled && !cancelled) setPalette(sampled)
    })
    return () => {
      cancelled = true
    }
  }, [props.frontImageUrl, props.paletteColor, props.samplePalette, props.palette])

  const resolvedPalette = props.palette ?? palette
  const tintVars = {
    '--slab-body-top': resolvedPalette.bodyTop,
    '--slab-body-mid': resolvedPalette.bodyMid,
    '--slab-body-bottom': resolvedPalette.bodyBottom,
    '--slab-border-top': resolvedPalette.borderTop,
    '--slab-border-bottom': resolvedPalette.borderBottom,
  } as CSSProperties

  const onKeyDown = (e: KeyboardEvent) => {
    if (!flipEnabled) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      flipRef.current()
    }
  }

  const imageFramesKnown =
    typeof props.frontImageRectificationApplied === 'boolean' &&
    typeof props.backImageRectificationApplied === 'boolean'
  const hasEmboss = Boolean(
    imageFramesKnown && props.embossFrontImageUrl && props.embossBackImageUrl,
  )
  return (
    <div className={`${styles.slabWrap} ${props.className ?? ''}`}>
      <div
        className={`${styles.stage} ${!flipEnabled ? styles.staticStage : ''}`}
        ref={stageRef}
        style={tintVars}
      >
        <div
          className={styles.flipper}
          role={flipEnabled ? 'button' : undefined}
          tabIndex={flipEnabled ? 0 : undefined}
          aria-label={flipEnabled ? 'Card slab — flip to see the other side' : undefined}
          onKeyDown={onKeyDown}
        >
          <div className={styles.border}>
            <div className={styles.panel}>
              <SlabHeader
                cardName={props.cardName}
                cardNumber={props.cardNumber}
                officialCount={props.officialCount}
                setName={props.setName}
                language={props.language}
                grade={props.grade}
                provider={props.provider}
                confidencePercent={props.confidencePercent}
                useInlineLogos={props.useInlineLogos}
              />
              <div className={styles.media}>
                <img
                  className={styles.cardPhoto}
                  style={{ opacity: 'var(--front-o, 1)' }}
                  src={props.frontImageUrl}
                  alt={`${props.cardName} — front`}
                  draggable={false}
                />
                {flipEnabled && <img
                  className={styles.cardPhoto}
                  style={{ opacity: 'var(--back-o, 0)' }}
                  src={props.backImageUrl}
                  alt={`${props.cardName} — back`}
                  draggable={false}
                />}
                {hasEmboss && props.embossFrontImageUrl && (
                  <div
                    className={styles.embossLayer}
                    style={{ opacity: 'var(--front-o, 1)' }}
                    aria-hidden="true"
                  >
                    <img
                      className={`${styles.cardPhoto} ${styles.embossPhoto}`}
                      style={{ opacity: surfaceView ? 1 : 0 }}
                      src={props.embossFrontImageUrl}
                      alt=""
                      draggable={false}
                    />
                  </div>
                )}
                {hasEmboss && props.embossBackImageUrl && (
                  <div
                    className={styles.embossLayer}
                    style={{ opacity: 'var(--back-o, 0)' }}
                    aria-hidden="true"
                  >
                    <img
                      className={`${styles.cardPhoto} ${styles.embossPhoto}`}
                      style={{ opacity: surfaceView ? 1 : 0 }}
                      src={props.embossBackImageUrl}
                      alt=""
                      draggable={false}
                    />
                  </div>
                )}
                <AnalysisOverlayCanvas
                  analysis={props.analysis?.front}
                  centering={props.centering?.front}
                  imageUrl={props.frontImageUrl}
                  rectified={props.frontImageRectificationApplied}
                  className={styles.frontOverlay}
                />
                {flipEnabled && <AnalysisOverlayCanvas
                  analysis={props.analysis?.back}
                  centering={props.centering?.back}
                  imageUrl={props.backImageUrl}
                  rectified={props.backImageRectificationApplied}
                  className={styles.backOverlay}
                />}
              </div>
            </div>
          </div>
        </div>
      </div>

      {props.showActions !== false && <div className={styles.actionRow}>
        <button
          className={styles.actionButton}
          onClick={() => flipRef.current()}
        >
          <FlipIcon size={18} />
          Flip
        </button>
        {hasEmboss && (
          <button
            className={`${styles.surfaceButton} ${surfaceView ? styles.surfaceButtonActive : ''}`}
            aria-pressed={surfaceView}
            onClick={() => setSurfaceView((v) => !v)}
          >
            Surface view
            <span className={styles.surfaceChip}>
              {surfaceView ? 'On' : 'Off'}
            </span>
          </button>
        )}
      </div>}
    </div>
  )
}
